/* ==========================================================================
   DIREVULF — hidden offline game  ("DOODLE ROAD" draw-a-path build)
   --------------------------------------------------------------------------
   Runs ONLY when the browser is genuinely offline (navigator.onLine +
   online/offline events). While online this file attaches two listeners and
   does nothing else: no canvas, no audio graph, no RAF loop, no level data.

   Zero network dependencies: every visual is drawn procedurally on a canvas
   and every sound is synthesised with the WebAudio API. No CDN, no fetch,
   no XHR, no iframe, no external image / font / audio file.

   Isolation: one global (window.DIREVULF_OFFLINE_GAME), one DOM node
   (#dvog-root), every class prefixed dvog-. Nothing in the storefront is
   read, rewritten or removed — the site is only scroll-locked behind the
   overlay and restored untouched on reconnect.
   ========================================================================== */
(function () {
  'use strict';

  if (window.DIREVULF_OFFLINE_GAME) { return; }

  var CFG = window.DVOG_CONFIG || {};
  var VERSION = '1.0.0';
  var DEBUG = !!CFG.debug;

  function log() {
    if (!DEBUG) { return; }
    try {
      console.log.apply(console, ['[dvog]'].concat(Array.prototype.slice.call(arguments)));
    } catch (e) { /* no-op */ }
  }

  /* ------------------------------------------------------------- helpers -- */
  var WORLD_W = 1500;
  var WORLD_H = 850;

  function clamp(v, a, b) { return v < a ? a : (v > b ? b : v); }
  function len(x, y) { return Math.sqrt(x * x + y * y); }

  function store(key, value) {
    try {
      if (value === undefined) { return window.localStorage.getItem('dvog:' + key); }
      window.localStorage.setItem('dvog:' + key, value);
    } catch (e) { /* private mode / blocked storage */ }
    return null;
  }

  /* --------------------------------------------------------------- audio -- */
  /* Synthesised only. The AudioContext is created on the first real user
     gesture, so autoplay policy is satisfied by construction; if it still
     starts suspended, every gesture calls resume(). */
  var Sound = {
    ctx: null,
    master: null,
    noiseBuf: null,
    roll: null,
    muted: store('muted') === '1',
    ready: false,

    init: function () {
      if (this.ctx) { return this.ctx; }
      var AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) { return null; }
      try {
        this.ctx = new AC();
        this.master = this.ctx.createGain();
        this.master.gain.value = this.muted ? 0 : 0.5;
        this.master.connect(this.ctx.destination);

        var n = this.ctx.sampleRate * 0.7;
        this.noiseBuf = this.ctx.createBuffer(1, n, this.ctx.sampleRate);
        var d = this.noiseBuf.getChannelData(0);
        for (var i = 0; i < n; i++) { d[i] = Math.random() * 2 - 1; }
        this.ready = true;
      } catch (e) { this.ctx = null; }
      return this.ctx;
    },

    unlock: function () {
      var c = this.init();
      if (c && c.state === 'suspended' && c.resume) {
        c.resume().catch(function () { /* ignored */ });
      }
    },

    setMuted: function (m) {
      this.muted = !!m;
      store('muted', this.muted ? '1' : '0');
      if (this.master) {
        var t = this.ctx.currentTime;
        this.master.gain.cancelScheduledValues(t);
        this.master.gain.setTargetAtTime(this.muted ? 0 : 0.5, t, 0.02);
      }
    },

    tone: function (freq, dur, type, gain, freqTo) {
      if (!this.ready || this.muted) { return; }
      var c = this.ctx, t = c.currentTime;
      var o = c.createOscillator();
      var g = c.createGain();
      o.type = type || 'square';
      o.frequency.setValueAtTime(freq, t);
      if (freqTo) { o.frequency.exponentialRampToValueAtTime(Math.max(20, freqTo), t + dur); }
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(gain === undefined ? 0.18 : gain, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
      o.connect(g); g.connect(this.master);
      o.start(t); o.stop(t + dur + 0.02);
    },

    burst: function (dur, cut, gain) {
      if (!this.ready || this.muted) { return; }
      var c = this.ctx, t = c.currentTime;
      var s = c.createBufferSource();
      s.buffer = this.noiseBuf;
      var f = c.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.setValueAtTime(cut || 1400, t);
      var g = c.createGain();
      g.gain.setValueAtTime(gain === undefined ? 0.2 : gain, t);
      g.gain.exponentialRampToValueAtTime(0.0008, t + dur);
      s.connect(f); f.connect(g); g.connect(this.master);
      s.start(t); s.stop(t + dur + 0.02);
    },

    click:  function () { this.tone(660, 0.05, 'square', 0.1); },
    inkTick: function () { this.tone(1500 + Math.random() * 500, 0.022, 'triangle', 0.045); },
    launch: function () { this.tone(180, 0.34, 'sawtooth', 0.16, 620); this.burst(0.2, 900, 0.1); },
    mark:   function () { this.tone(880, 0.08, 'triangle', 0.16); this.tone(1320, 0.12, 'triangle', 0.12); },
    thud:   function () { this.burst(0.16, 320, 0.22); this.tone(90, 0.16, 'sine', 0.14, 50); },
    fail:   function () { this.tone(300, 0.5, 'sawtooth', 0.16, 70); this.burst(0.4, 520, 0.18); },
    win:    function () {
      var self = this, notes = [523, 659, 784, 1046];
      notes.forEach(function (f, i) {
        window.setTimeout(function () { self.tone(f, 0.26, 'triangle', 0.15); }, i * 92);
      });
    },

    /* engine / rolling loop, gain driven by wheel speed */
    startRoll: function () {
      if (!this.ready || this.roll) { return; }
      var c = this.ctx;
      var s = c.createBufferSource();
      s.buffer = this.noiseBuf;
      s.loop = true;
      var f = c.createBiquadFilter();
      f.type = 'lowpass';
      f.frequency.value = 420;
      var g = c.createGain();
      g.gain.value = 0;
      s.connect(f); f.connect(g); g.connect(this.master);
      s.start(0);
      this.roll = { src: s, gain: g, filter: f };
    },
    setRoll: function (speed) {
      if (!this.roll) { return; }
      var t = this.ctx.currentTime;
      var k = clamp(speed / 620, 0, 1);
      this.roll.gain.gain.setTargetAtTime(k * 0.13, t, 0.06);
      this.roll.filter.frequency.setTargetAtTime(280 + k * 900, t, 0.06);
    },
    stopRoll: function () {
      if (!this.roll) { return; }
      try { this.roll.src.stop(0); } catch (e) { /* already stopped */ }
      try { this.roll.src.disconnect(); this.roll.gain.disconnect(); } catch (e) { /* no-op */ }
      this.roll = null;
    },

    destroy: function () {
      this.stopRoll();
      if (this.ctx && this.ctx.close) {
        try { this.ctx.close(); } catch (e) { /* no-op */ }
      }
      this.ctx = null; this.master = null; this.noiseBuf = null; this.ready = false;
    }
  };

  /* -------------------------------------------------------------- levels -- */
  /* Copy uses only real DIREVULF areas: JOIN THE PACK, Shop by Fandom,
     Create Your Own, fandom clothing, accessories, Did You Know, Bulk Orders,
     Beyond the Pack (community initiatives). No invented facts or figures. */

  function seg(x1, y1, x2, y2) { return { x1: x1, y1: y1, x2: x2, y2: y2 }; }

  function box(x, y, w, h) {
    return [
      seg(x, y, x + w, y),
      seg(x + w, y, x + w, y + h),
      seg(x + w, y + h, x, y + h),
      seg(x, y + h, x, y)
    ];
  }

  function spikes(x, y, w) { return { x: x, y: y, w: w }; }

  var LEVELS = [
    {
      name: 'JOIN THE PACK',
      hint: 'Draw a road. Get the crate to the flag.',
      ink: 1500, engine: true,
      start: { x: 190, y: 520 },
      ground: box(90, 560, 240, 30),
      solids: [], hazards: [],
      finish: { x: 1240, y: 520, r: 46 },
      pad: box(1120, 560, 300, 30),
      marks: [{ x: 700, y: 470 }]
    },
    {
      name: 'SHOP BY FANDOM',
      hint: 'Three universes, one road. Mind the drop.',
      ink: 1450, engine: true,
      start: { x: 180, y: 340 },
      ground: box(90, 380, 230, 26),
      solids: [], hazards: [spikes(430, 800, 620)],
      finish: { x: 1250, y: 600, r: 46 },
      pad: box(1120, 640, 300, 26),
      marks: [{ x: 620, y: 520 }, { x: 900, y: 600 }]
    },
    {
      name: 'CREATE YOUR OWN',
      hint: 'Your line, your build. Collect all three marks.',
      ink: 1750, engine: true,
      start: { x: 170, y: 250 },
      ground: box(90, 290, 210, 24),
      solids: box(640, 430, 240, 26),
      hazards: [spikes(340, 810, 1000)],
      finish: { x: 1270, y: 300, r: 46 },
      pad: box(1150, 340, 280, 24),
      marks: [{ x: 480, y: 380 }, { x: 760, y: 360 }, { x: 1040, y: 330 }]
    },
    {
      name: 'FANDOM CLOTHING',
      hint: 'Low clearance. Keep the crate under the rail.',
      ink: 1500, engine: true,
      start: { x: 175, y: 560 },
      ground: box(90, 600, 220, 26),
      solids: box(520, 350, 520, 190),
      hazards: [spikes(330, 812, 180), spikes(1060, 812, 120)],
      finish: { x: 1280, y: 560, r: 46 },
      pad: box(1150, 600, 280, 26),
      marks: [{ x: 640, y: 620 }, { x: 920, y: 620 }]
    },
    {
      name: 'ACCESSORIES',
      hint: 'Small pieces, tight budget. Spend ink carefully.',
      ink: 1100, engine: true,
      start: { x: 170, y: 220 },
      ground: box(90, 260, 200, 22),
      solids: box(480, 470, 150, 22).concat(box(830, 620, 150, 22)),
      hazards: [spikes(320, 812, 1040)],
      finish: { x: 1270, y: 700, r: 46 },
      pad: box(1150, 740, 280, 22),
      marks: [{ x: 540, y: 420 }, { x: 890, y: 570 }]
    },
    {
      name: 'DID YOU KNOW',
      hint: 'Engine off. Gravity is the only motor you get.',
      ink: 1900, engine: false,
      start: { x: 130, y: 150 },
      /* engine off means gravity has to start the crate, so this level's
         launch platform is a slope rather than a flat deck. index 0 is the
         collision surface; the rest is the slab body. */
      ground: [
        seg(70, 186, 300, 252),
        seg(300, 252, 300, 274),
        seg(300, 274, 70, 208),
        seg(70, 208, 70, 186)
      ],
      solids: [],
      hazards: [spikes(300, 812, 1080)],
      finish: { x: 1270, y: 640, r: 48 },
      pad: box(1140, 680, 290, 22),
      marks: [{ x: 560, y: 430 }, { x: 880, y: 560 }]
    },
    {
      name: 'BULK ORDERS',
      hint: 'Heavier crate, longer haul. Build it strong.',
      ink: 2000, engine: true, heavy: true,
      start: { x: 170, y: 300 },
      ground: box(90, 340, 210, 24),
      solids: box(700, 560, 200, 24),
      hazards: [spikes(320, 812, 1060)],
      finish: { x: 1280, y: 380, r: 48 },
      pad: box(1150, 420, 280, 24),
      marks: [{ x: 520, y: 440 }, { x: 800, y: 500 }, { x: 1080, y: 420 }]
    },
    {
      name: 'BEYOND THE PACK',
      hint: 'The long one. Every mark counts.',
      ink: 1700, engine: true,
      start: { x: 165, y: 190 },
      ground: box(85, 230, 200, 22),
      /* the run-out ledge is crossed over the top; the spike bed past it has to
         be cleared high and left late, which is the whole puzzle */
      solids: box(520, 430, 260, 22),
      hazards: [spikes(300, 812, 1100), spikes(840, 560, 140)],
      finish: { x: 1290, y: 640, r: 48 },
      pad: box(1160, 680, 280, 22),
      marks: [{ x: 450, y: 372 }, { x: 700, y: 396 }, { x: 985, y: 500 }, { x: 1120, y: 622 }]
    }
  ];

  var TOTAL_MARKS = LEVELS.reduce(function (n, l) { return n + l.marks.length; }, 0);

  /* ========================================================================
     GAME
     ====================================================================== */
  function Game(canvas, hooks) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.hooks = hooks || {};
    this.dpr = 1;
    this.scale = 1;
    this.offX = 0;
    this.offY = 0;
    this.raf = 0;
    this.last = 0;
    this.acc = 0;
    this.time = 0;
    this.destroyed = false;

    this.state = 'draw';            /* draw | run | won | dead */
    this.levelIndex = 0;
    this.level = null;
    this.segments = [];
    this.strokes = [];
    this.drawing = null;
    this.inkUsed = 0;
    this.particles = [];
    this.shake = 0;
    this.flash = 0;
    this.runTime = 0;
    this.totalTime = 0;
    this.marksGot = 0;
    this.attempts = 0;
    this.lastTickAt = 0;
    this.stall = 0;

    this.rider = {
      x: 0, y: 0, vx: 0, vy: 0, r: 20, angle: 0, spin: 0,
      grounded: false, gx: 1, gy: 0, contact: 0
    };
  }

  Game.prototype.loadLevel = function (i) {
    var lv = LEVELS[i];
    this.levelIndex = i;
    this.level = lv;
    this.strokes = [];
    this.inkUsed = 0;
    this.drawing = null;
    this.particles = [];
    this.runTime = 0;
    this.attempts = 0;
    this.marksGot = 0;
    this.state = 'draw';

    /* static geometry: pads + ground + solids, plus hazard strips as segments */
    this.statics = [];   /* collision */
    this.decor = [];     /* what is actually drawn */
    var copy = function (s2, deadly) {
      return { x1: s2.x1, y1: s2.y1, x2: s2.x2, y2: s2.y2, deadly: !!deadly };
    };
    var push = function (arr, list, deadly) {
      for (var k = 0; k < list.length; k++) { arr.push(copy(list[k], deadly)); }
    };

    /* Start and finish platforms collide on their TOP surface only. A closed
       box would present a vertical side wall that stops a crate arriving at
       deck height, which is not how a platform should behave. Obstacles keep
       full box collision, since blocking is their whole job. */
    push(this.decor, lv.ground, false);
    push(this.decor, lv.pad, false);
    push(this.decor, lv.solids || [], false);
    this.statics.push(copy(lv.ground[0], false));
    this.statics.push(copy(lv.pad[0], false));
    push(this.statics, lv.solids || [], false);

    /* On-ramp at the finish deck's leading edge. A hand-drawn road rarely lands
       exactly at deck height, and without this the crate wedges against the
       square corner a few pixels short of the flag — the most frustrating way
       a run can end, and not one the player caused. */
    var pTop = lv.pad[0];
    var ramp = { x1: pTop.x1 - 34, y1: pTop.y1 + 20, x2: pTop.x1, y2: pTop.y1, deadly: false };
    this.statics.push(ramp);
    this.decor.push({ x1: ramp.x1, y1: ramp.y1, x2: ramp.x2, y2: ramp.y2, deadly: false });
    for (var h = 0; h < lv.hazards.length; h++) {
      var hz = lv.hazards[h];
      this.statics.push({ x1: hz.x, y1: hz.y, x2: hz.x + hz.w, y2: hz.y, deadly: true });
    }

    /* a duplicated segment would resolve the same contact twice and double its
       friction, so collapse identical lines */
    var seen = {};
    this.statics = this.statics.filter(function (s2) {
      var key = s2.x1 + ':' + s2.y1 + ':' + s2.x2 + ':' + s2.y2 + ':' + (s2.deadly ? 1 : 0);
      if (seen[key]) { return false; }
      seen[key] = 1;
      return true;
    });

    /* stable hand-drawn jitter so the doodle look does not crawl per frame */
    for (var s = 0; s < this.decor.length; s++) {
      this.decor[s].j = [
        (Math.random() - 0.5) * 5, (Math.random() - 0.5) * 5,
        (Math.random() - 0.5) * 5, (Math.random() - 0.5) * 5
      ];
    }

    this.marks = lv.marks.map(function (m) { return { x: m.x, y: m.y, got: false, t: Math.random() * 6 }; });
    this.resetRider();
    this.emit();
  };

  Game.prototype.resetRider = function () {
    var lv = this.level;
    var r = this.rider;
    r.x = lv.start.x;
    r.y = lv.start.y;
    r.vx = 0; r.vy = 0;
    r.angle = 0; r.spin = 0;
    r.r = lv.heavy ? 26 : 20;
    r.grounded = false;
    r.contact = 0;
    this.runTime = 0;
    this.stall = 0;
    for (var i = 0; i < this.marks.length; i++) { this.marks[i].got = false; }
    this.marksGot = 0;
  };

  Game.prototype.inkLeft = function () { return Math.max(0, this.level.ink - this.inkUsed); };

  Game.prototype.emit = function () {
    if (this.hooks.onState) { this.hooks.onState(this); }
  };

  /* ------------------------------------------------------------- drawing -- */
  Game.prototype.beginStroke = function (p) {
    if (this.state === 'won') { return; }
    if (this.inkLeft() <= 4) { return; }
    this.drawing = { pts: [{ x: p.x, y: p.y }], length: 0 };
  };

  Game.prototype.extendStroke = function (p) {
    var d = this.drawing;
    if (!d) { return; }
    var last = d.pts[d.pts.length - 1];
    var dx = p.x - last.x, dy = p.y - last.y;
    var dist = len(dx, dy);
    if (dist < 9) { return; }

    var left = this.inkLeft();
    if (left <= 0) { this.endStroke(); return; }
    if (dist > left) {
      var k = left / dist;
      p = { x: last.x + dx * k, y: last.y + dy * k };
      dist = left;
    }
    d.pts.push({ x: p.x, y: p.y });
    d.length += dist;
    this.inkUsed += dist;

    var now = this.time;
    if (now - this.lastTickAt > 0.045) { this.lastTickAt = now; Sound.inkTick(); }
    if (this.inkLeft() <= 0) { this.endStroke(); }
    this.emit();
  };

  Game.prototype.endStroke = function () {
    var d = this.drawing;
    this.drawing = null;
    if (!d || d.pts.length < 2) {
      if (d) { this.inkUsed -= d.length; }
      this.emit();
      return;
    }
    this.strokes.push(d);
    this.emit();
  };

  Game.prototype.undo = function () {
    if (this.drawing) { this.inkUsed -= this.drawing.length; this.drawing = null; }
    else {
      var s = this.strokes.pop();
      if (s) { this.inkUsed -= s.length; }
    }
    this.inkUsed = Math.max(0, this.inkUsed);
    Sound.click();
    this.emit();
  };

  Game.prototype.clearInk = function () {
    this.strokes = [];
    this.drawing = null;
    this.inkUsed = 0;
    Sound.click();
    this.emit();
  };

  /* --------------------------------------------------------------- states -- */
  Game.prototype.launch = function () {
    if (this.state === 'run') { return; }
    this.state = 'run';
    this.attempts++;
    this.resetRider();
    Sound.unlock();
    Sound.launch();
    if (this.level.engine) { Sound.startRoll(); }
    this.emit();
  };

  Game.prototype.retry = function () {
    this.state = 'draw';
    Sound.stopRoll();
    this.resetRider();
    Sound.click();
    this.emit();
  };

  Game.prototype.die = function () {
    if (this.state !== 'run') { return; }
    this.state = 'dead';
    this.shake = 1;
    this.flash = 1;
    Sound.stopRoll();
    Sound.fail();
    for (var i = 0; i < 26; i++) {
      this.particles.push({
        x: this.rider.x, y: this.rider.y,
        vx: (Math.random() - 0.5) * 520, vy: (Math.random() - 0.9) * 520,
        life: 0.7 + Math.random() * 0.5, max: 1.2, c: '#ff0000', s: 2 + Math.random() * 3
      });
    }
    var self = this;
    this.deadTimer = window.setTimeout(function () {
      if (self.destroyed) { return; }
      if (self.state === 'dead') { self.state = 'draw'; self.resetRider(); self.emit(); }
    }, 850);
    this.emit();
  };

  Game.prototype.win = function () {
    if (this.state !== 'run') { return; }
    this.state = 'won';
    this.totalTime += this.runTime;
    Sound.stopRoll();
    Sound.win();
    for (var i = 0; i < 40; i++) {
      this.particles.push({
        x: this.level.finish.x, y: this.level.finish.y,
        vx: (Math.random() - 0.5) * 460, vy: (Math.random() - 1.1) * 460,
        life: 0.8 + Math.random() * 0.7, max: 1.5,
        c: Math.random() > 0.5 ? '#ff0000' : '#ffffff', s: 2 + Math.random() * 3
      });
    }
    if (this.hooks.onWin) { this.hooks.onWin(this); }
    this.emit();
  };

  /* ------------------------------------------------------------- physics -- */
  Game.prototype.collect = function () {
    var r = this.rider;
    for (var i = 0; i < this.marks.length; i++) {
      var m = this.marks[i];
      if (m.got) { continue; }
      if (len(m.x - r.x, m.y - r.y) < r.r + 22) {
        m.got = true;
        this.marksGot++;
        Sound.mark();
        for (var k = 0; k < 10; k++) {
          this.particles.push({
            x: m.x, y: m.y,
            vx: (Math.random() - 0.5) * 260, vy: (Math.random() - 0.5) * 260,
            life: 0.4 + Math.random() * 0.3, max: 0.7, c: '#ff0000', s: 1.6 + Math.random() * 2
          });
        }
      }
    }
  };

  function hit(px, py, rad, s) {
    var abx = s.x2 - s.x1, aby = s.y2 - s.y1;
    var l2 = abx * abx + aby * aby;
    var t = l2 > 0 ? ((px - s.x1) * abx + (py - s.y1) * aby) / l2 : 0;
    t = clamp(t, 0, 1);
    var cx = s.x1 + abx * t, cy = s.y1 + aby * t;
    var dx = px - cx, dy = py - cy;
    var d2 = dx * dx + dy * dy;
    if (d2 > rad * rad) { return null; }
    var d = Math.sqrt(d2);
    if (d < 0.0001) { return { nx: 0, ny: -1, pen: rad }; }
    return { nx: dx / d, ny: dy / d, pen: rad - d };
  }

  Game.prototype.step = function (dt) {
    var r = this.rider;
    var lv = this.level;
    var GRAV = 2050;
    var REST = 0.14;
    /* Rolling resistance, applied per substep at 240 Hz. Kept low on purpose:
       a wheel does not scrub, and the engine-off level has nothing but
       momentum to finish on — heavy damping strands it short of the flag.
       Top speed is governed by MAXV, not by friction. */
    var FRIC = 0.005;
    var MOTOR = lv.heavy ? 1900 : 2400;
    var MAXV = lv.heavy ? 540 : 620;

    r.vy += GRAV * dt;
    r.vx *= (1 - 0.10 * dt);
    r.x += r.vx * dt;
    r.y += r.vy * dt;

    r.grounded = false;
    var deadly = false;

    /* collide against level statics, then against every drawn ink segment */
    var i, k, c;
    for (i = 0; i < this.statics.length; i++) {
      c = hit(r.x, r.y, r.r, this.statics[i]);
      if (c) {
        if (this.statics[i].deadly) { deadly = true; }
        this.resolve(c, REST, FRIC);
      }
    }
    for (i = 0; i < this.strokes.length; i++) {
      var pts = this.strokes[i].pts;
      for (k = 0; k < pts.length - 1; k++) {
        c = hit(r.x, r.y, r.r, { x1: pts[k].x, y1: pts[k].y, x2: pts[k + 1].x, y2: pts[k + 1].y });
        if (c) { this.resolve(c, REST, FRIC * 1.4); }
      }
    }

    /* engine torque along the contact tangent, forward (+x) only */
    if (lv.engine && r.grounded) {
      /* r.gx / r.gy already hold the unit tangent of the contact surface;
         drive along it, forward (+x). */
      var tx = r.gx, ty = r.gy;
      if (tx < 0) { tx = -tx; ty = -ty; }
      var sp = r.vx * tx + r.vy * ty;
      if (sp < MAXV) {
        r.vx += tx * MOTOR * dt;
        r.vy += ty * MOTOR * dt;
      }
    }

    r.spin = r.grounded ? (r.vx * r.gx + r.vy * r.gy) / r.r : r.spin * 0.995;
    r.angle += r.spin * dt;

    /* a crate that has stopped moving would otherwise leave the run hanging
       forever with no way out but the RETRY button */
    if (len(r.vx, r.vy) < 14) {
      this.stall += dt;
      if (this.stall > 2.6) { this.stall = 0; this.die(); return; }
    } else {
      this.stall = 0;
    }

    this.collect();

    if (deadly) { this.die(); return; }
    if (r.y > WORLD_H + 260 || r.x < -240 || r.x > WORLD_W + 320) { this.die(); return; }

    var f = lv.finish;
    if (len(f.x - r.x, f.y - r.y) < f.r) { this.win(); }
  };

  Game.prototype.resolve = function (c, rest, fric) {
    var r = this.rider;
    r.x += c.nx * c.pen;
    r.y += c.ny * c.pen;

    var vn = r.vx * c.nx + r.vy * c.ny;
    if (vn < 0) {
      if (vn < -320 && this.time - r.contact > 0.14) {
        r.contact = this.time;
        Sound.thud();
        for (var p = 0; p < 5; p++) {
          this.particles.push({
            x: r.x, y: r.y + r.r * 0.4,
            vx: (Math.random() - 0.5) * 130, vy: -Math.random() * 90,
            life: 0.3, max: 0.3, c: '#6a6a6a', s: 1.4 + Math.random() * 1.6
          });
        }
      }
      r.vx -= c.nx * vn * (1 + rest);
      r.vy -= c.ny * vn * (1 + rest);
    }

    var tx = -c.ny, ty = c.nx;
    var vt = r.vx * tx + r.vy * ty;
    r.vx -= tx * vt * fric;
    r.vy -= ty * vt * fric;

    if (c.ny < -0.35) {
      r.grounded = true;
      r.gx = tx; r.gy = ty;
      if (r.gx < 0) { r.gx = -r.gx; r.gy = -r.gy; }
    }
  };

  /* ---------------------------------------------------------------- loop -- */
  Game.prototype.frame = function (now) {
    if (this.destroyed) { return; }
    if (!this.last) { this.last = now; }
    var dt = Math.min(0.05, (now - this.last) / 1000);
    this.last = now;
    this.time += dt;

    if (this.state === 'run') {
      this.runTime += dt;
      this.acc += dt;
      var fixed = 1 / 240;
      var guard = 0;
      while (this.acc >= fixed && guard < 40) {
        this.step(fixed);
        this.acc -= fixed;
        guard++;
        if (this.state !== 'run') { this.acc = 0; break; }
      }
      Sound.setRoll(Math.abs(this.rider.vx));
    } else {
      this.acc = 0;
      Sound.setRoll(0);
    }

    for (var i = this.particles.length - 1; i >= 0; i--) {
      var p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) { this.particles.splice(i, 1); continue; }
      p.vy += 1300 * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
    }

    this.shake = Math.max(0, this.shake - dt * 3.2);
    this.flash = Math.max(0, this.flash - dt * 2.6);

    for (var m = 0; m < this.marks.length; m++) { this.marks[m].t += dt * 2.4; }

    this.render();
    var self = this;
    this.raf = window.requestAnimationFrame(function (t) { self.frame(t); });
  };

  Game.prototype.start = function () {
    var self = this;
    this.last = 0;
    this.raf = window.requestAnimationFrame(function (t) { self.frame(t); });
  };

  /* -------------------------------------------------------------- resize -- */
  Game.prototype.resize = function () {
    var rect = this.canvas.parentNode.getBoundingClientRect();
    var w = Math.max(1, Math.floor(rect.width));
    var h = Math.max(1, Math.floor(rect.height));
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.canvas.width = Math.floor(w * this.dpr);
    this.canvas.height = Math.floor(h * this.dpr);
    this.cssW = w;
    this.cssH = h;
    this.scale = Math.min(w / WORLD_W, h / WORLD_H);
    this.offX = (w - WORLD_W * this.scale) / 2;
    this.offY = (h - WORLD_H * this.scale) / 2;
  };

  Game.prototype.toWorld = function (clientX, clientY) {
    var rect = this.canvas.getBoundingClientRect();
    return {
      x: (clientX - rect.left - this.offX) / this.scale,
      y: (clientY - rect.top - this.offY) / this.scale
    };
  };

  /* -------------------------------------------------------------- render -- */
  Game.prototype.render = function () {
    var g = this.ctx;
    var lv = this.level;

    g.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    g.clearRect(0, 0, this.cssW, this.cssH);
    g.fillStyle = '#000000';
    g.fillRect(0, 0, this.cssW, this.cssH);

    var sx = 0, sy = 0;
    if (this.shake > 0) {
      sx = (Math.random() - 0.5) * 16 * this.shake;
      sy = (Math.random() - 0.5) * 16 * this.shake;
    }

    g.save();
    g.translate(this.offX + sx, this.offY + sy);
    g.scale(this.scale, this.scale);

    /* graph-paper ground plane — the doodle surface, drawn not fetched */
    g.fillStyle = '#050505';
    g.fillRect(0, 0, WORLD_W, WORLD_H);
    g.strokeStyle = 'rgba(255,255,255,0.035)';
    g.lineWidth = 1;
    g.beginPath();
    for (var gx = 0; gx <= WORLD_W; gx += 50) { g.moveTo(gx, 0); g.lineTo(gx, WORLD_H); }
    for (var gy = 0; gy <= WORLD_H; gy += 50) { g.moveTo(0, gy); g.lineTo(WORLD_W, gy); }
    g.stroke();

    /* watermark: brand only, no invented claims */
    g.save();
    g.globalAlpha = 0.05;
    g.fillStyle = '#ffffff';
    g.font = '800 150px "Plus Jakarta Sans", Arial, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('DIREVULF', WORLD_W / 2, WORLD_H / 2 - 40);
    g.restore();

    this.drawStatics(g);
    this.drawFinish(g, lv.finish);
    this.drawMarks(g);
    this.drawInk(g);
    this.drawRider(g);
    this.drawParticles(g);

    if (this.state === 'draw') { this.drawGhostHint(g); }

    g.restore();

    if (this.flash > 0) {
      g.fillStyle = 'rgba(255,0,0,' + (this.flash * 0.28).toFixed(3) + ')';
      g.fillRect(0, 0, this.cssW, this.cssH);
    }
  };

  Game.prototype.drawStatics = function (g) {
    var i, s;
    /* hazard spikes first, so platform edges sit on top */
    for (i = 0; i < this.level.hazards.length; i++) {
      var hz = this.level.hazards[i];
      g.fillStyle = '#ff0000';
      g.beginPath();
      var step = 26;
      for (var x = hz.x; x < hz.x + hz.w; x += step) {
        var w2 = Math.min(step, hz.x + hz.w - x);
        g.moveTo(x, hz.y);
        g.lineTo(x + w2 / 2, hz.y - 22);
        g.lineTo(x + w2, hz.y);
      }
      g.closePath();
      g.fill();
      g.fillStyle = 'rgba(255,0,0,0.16)';
      g.fillRect(hz.x, hz.y, hz.w, WORLD_H - hz.y + 40);
    }

    g.lineCap = 'round';
    g.lineJoin = 'round';
    for (i = 0; i < this.decor.length; i++) {
      s = this.decor[i];
      if (s.deadly) { continue; }
      g.strokeStyle = '#f2f2f2';
      g.lineWidth = 7;
      g.beginPath();
      g.moveTo(s.x1 + s.j[0], s.y1 + s.j[1]);
      g.lineTo(s.x2 + s.j[2], s.y2 + s.j[3]);
      g.stroke();
      g.strokeStyle = 'rgba(255,255,255,0.14)';
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(s.x1 - s.j[0], s.y1 - s.j[1] + 5);
      g.lineTo(s.x2 - s.j[2], s.y2 - s.j[3] + 5);
      g.stroke();
    }
  };

  Game.prototype.drawFinish = function (g, f) {
    var wob = Math.sin(this.time * 4) * 5;
    g.strokeStyle = '#f2f2f2';
    g.lineWidth = 5;
    g.beginPath();
    g.moveTo(f.x, f.y + 40);
    g.lineTo(f.x, f.y - 62);
    g.stroke();

    g.fillStyle = '#ff0000';
    g.beginPath();
    g.moveTo(f.x, f.y - 62);
    g.lineTo(f.x + 58 + wob, f.y - 46);
    g.lineTo(f.x, f.y - 26);
    g.closePath();
    g.fill();

    g.strokeStyle = 'rgba(255,0,0,' + (0.24 + Math.sin(this.time * 3) * 0.1).toFixed(3) + ')';
    g.lineWidth = 2;
    g.beginPath();
    g.arc(f.x, f.y, f.r, 0, Math.PI * 2);
    g.stroke();
  };

  Game.prototype.drawMarks = function (g) {
    for (var i = 0; i < this.marks.length; i++) {
      var m = this.marks[i];
      if (m.got) { continue; }
      var s = 12 + Math.sin(m.t) * 2.4;
      g.save();
      g.translate(m.x, m.y);
      g.rotate(m.t * 0.5);
      g.fillStyle = '#ff0000';
      g.beginPath();
      g.moveTo(0, -s); g.lineTo(s, 0); g.lineTo(0, s); g.lineTo(-s, 0);
      g.closePath();
      g.fill();
      g.strokeStyle = 'rgba(255,255,255,0.5)';
      g.lineWidth = 1.5;
      g.stroke();
      g.restore();
    }
  };

  Game.prototype.drawInk = function (g) {
    var all = this.strokes.slice();
    if (this.drawing) { all.push(this.drawing); }
    g.lineCap = 'round';
    g.lineJoin = 'round';
    for (var i = 0; i < all.length; i++) {
      var pts = all[i].pts;
      if (pts.length < 2) { continue; }
      g.strokeStyle = 'rgba(255,0,0,0.30)';
      g.lineWidth = 16;
      g.beginPath();
      g.moveTo(pts[0].x, pts[0].y);
      for (var k = 1; k < pts.length; k++) { g.lineTo(pts[k].x, pts[k].y); }
      g.stroke();
      g.strokeStyle = '#ff2020';
      g.lineWidth = 8;
      g.stroke();
      g.strokeStyle = 'rgba(255,255,255,0.55)';
      g.lineWidth = 2;
      g.stroke();
    }
  };

  Game.prototype.drawRider = function (g) {
    var r = this.rider;
    g.save();
    g.translate(r.x, r.y);

    g.fillStyle = 'rgba(0,0,0,0.5)';
    g.beginPath();
    g.arc(0, 0, r.r + 5, 0, Math.PI * 2);
    g.fill();

    g.rotate(r.angle);

    /* wheel body */
    g.fillStyle = '#111111';
    g.beginPath();
    g.arc(0, 0, r.r, 0, Math.PI * 2);
    g.fill();
    g.strokeStyle = '#ff0000';
    g.lineWidth = 4;
    g.stroke();

    /* spokes */
    g.strokeStyle = 'rgba(255,255,255,0.34)';
    g.lineWidth = 2;
    for (var i = 0; i < 4; i++) {
      var a = (Math.PI / 2) * i;
      g.beginPath();
      g.moveTo(Math.cos(a) * (r.r - 4), Math.sin(a) * (r.r - 4));
      g.lineTo(Math.cos(a) * 4, Math.sin(a) * 4);
      g.stroke();
    }

    /* wolf-head glyph — original DIREVULF mark, drawn with paths */
    var k = r.r / 20;
    g.fillStyle = '#f2f2f2';
    g.beginPath();
    g.moveTo(-8 * k, -2 * k);
    g.lineTo(-9 * k, -9 * k);
    g.lineTo(-3.2 * k, -5.4 * k);
    g.lineTo(3.2 * k, -5.4 * k);
    g.lineTo(9 * k, -9 * k);
    g.lineTo(8 * k, -2 * k);
    g.lineTo(0, 8 * k);
    g.closePath();
    g.fill();
    g.fillStyle = '#ff0000';
    g.beginPath();
    g.arc(-3.4 * k, -1.4 * k, 1.5 * k, 0, Math.PI * 2);
    g.arc(3.4 * k, -1.4 * k, 1.5 * k, 0, Math.PI * 2);
    g.fill();

    g.restore();
  };

  Game.prototype.drawParticles = function (g) {
    for (var i = 0; i < this.particles.length; i++) {
      var p = this.particles[i];
      g.globalAlpha = clamp(p.life / p.max, 0, 1);
      g.fillStyle = p.c;
      g.fillRect(p.x - p.s / 2, p.y - p.s / 2, p.s, p.s);
    }
    g.globalAlpha = 1;
  };

  Game.prototype.drawGhostHint = function (g) {
    if (this.strokes.length || this.drawing) { return; }
    var a = 0.28 + Math.sin(this.time * 2.4) * 0.12;
    g.strokeStyle = 'rgba(255,255,255,' + a.toFixed(3) + ')';
    g.lineWidth = 3;
    g.setLineDash([12, 12]);
    g.beginPath();
    g.moveTo(this.level.start.x + 40, this.level.start.y + 44);
    g.lineTo(this.level.start.x + 190, this.level.start.y + 60);
    g.stroke();
    g.setLineDash([]);

    g.fillStyle = 'rgba(255,255,255,' + (a + 0.2).toFixed(3) + ')';
    g.font = '700 20px "Plus Jakarta Sans", Arial, sans-serif';
    g.textAlign = 'left';
    g.fillText('DRAG TO DRAW', this.level.start.x + 44, this.level.start.y + 96);
  };

  Game.prototype.destroy = function () {
    this.destroyed = true;
    if (this.raf) { window.cancelAnimationFrame(this.raf); this.raf = 0; }
    if (this.deadTimer) { window.clearTimeout(this.deadTimer); this.deadTimer = 0; }
    this.particles = [];
    this.strokes = [];
    this.statics = [];
    this.decor = [];
    this.marks = [];
    this.ctx = null;
  };

  /* ========================================================================
     SHELL — DOM, input, offline controller
     ====================================================================== */
  var ICON = {
    sound: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9h3l4-4v14l-4-4H4V9zm11.5-.5a5 5 0 0 1 0 7v-2a3 3 0 0 0 0-3v-2z"/></svg>',
    muted: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 9h3l4-4v14l-4-4H4V9zm10.3 1.3 1.4-1.4 2.1 2.1 2.1-2.1 1.4 1.4-2.1 2.1 2.1 2.1-1.4 1.4-2.1-2.1-2.1 2.1-1.4-1.4 2.1-2.1-2.1-2.1z"/></svg>',
    play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5l12 7-12 7z"/></svg>',
    undo: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 7V3L3 8l6 5V9h5a4 4 0 1 1 0 8h-3v3h3a7 7 0 0 0 0-14H9z"/></svg>',
    reset: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5V2L7 6l5 4V7a5 5 0 1 1-5 5H4a8 8 0 1 0 8-7z"/></svg>',
    clear: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 7h12l-1 13H7L6 7zm3-4h6l1 2H8l1-2z"/></svg>'
  };

  var Shell = {
    active: false,
    booting: false,
    root: null,
    game: null,
    listeners: [],
    timers: [],
    screen: null,
    reason: '',

    on: function (target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.listeners.push([target, type, fn, opts]);
    },

    later: function (fn, ms) {
      var id = window.setTimeout(fn, ms);
      this.timers.push(id);
      return id;
    },

    /* ------------------------------------------------------------ markup -- */
    build: function () {
      var root = document.createElement('div');
      root.id = 'dvog-root';
      root.setAttribute('role', 'application');
      root.setAttribute('aria-label', 'DIREVULF offline game');
      root.innerHTML = [
        '<div class="dvog-bar">',
        '  <div class="dvog-wordmark">DIRE<span>VULF</span></div>',
        '  <div class="dvog-tagline">JOIN THE PACK</div>',
        '  <div class="dvog-bar-spacer"></div>',
        '  <div class="dvog-chip dvog-chip-rotate" data-dvog-rotate hidden>ROTATE</div>',
        '  <div class="dvog-chip dvog-chip-status"><i class="dvog-dot"></i><span>OFFLINE</span></div>',
        '  <button type="button" class="dvog-iconbtn" data-dvog-mute aria-label="Toggle sound"></button>',
        '</div>',
        '<div class="dvog-stage">',
        '  <canvas class="dvog-canvas" data-dvog-canvas></canvas>',
        '  <div class="dvog-hud">',
        '    <div class="dvog-hud-row">',
        '      <div class="dvog-panel">',
        '        <div class="dvog-level-name" data-dvog-level>—</div>',
        '        <div class="dvog-level-sub" data-dvog-hint>—</div>',
        '      </div>',
        '      <div class="dvog-bar-spacer"></div>',
        '      <div class="dvog-panel dvog-ink-wrap">',
        '        <div class="dvog-ink-label"><span>INK</span><span data-dvog-ink>0</span></div>',
        '        <div class="dvog-ink-track"><i class="dvog-ink-fill" data-dvog-inkfill></i></div>',
        '      </div>',
        '    </div>',
        '    <div class="dvog-hud-row dvog-bottom">',
        '      <div class="dvog-keys">',
        '        <span class="dvog-key"><b>DRAG</b> DRAW</span>',
        '        <span class="dvog-key"><b>SPACE</b> LAUNCH</span>',
        '        <span class="dvog-key"><b>R</b> RETRY</span>',
        '        <span class="dvog-key"><b>U</b> UNDO</span>',
        '        <span class="dvog-key"><b>C</b> CLEAR</span>',
        '        <span class="dvog-key"><b>M</b> SOUND</span>',
        '        <span class="dvog-key"><b>ESC</b> PAUSE</span>',
        '      </div>',
        '      <div class="dvog-bar-spacer"></div>',
        '      <div class="dvog-actions">',
        '        <button type="button" class="dvog-btn dvog-btn-icon" data-dvog-undo>' + ICON.undo + '<i>UNDO</i></button>',
        '        <button type="button" class="dvog-btn dvog-btn-icon" data-dvog-clear>' + ICON.clear + '<i>CLEAR</i></button>',
        '        <button type="button" class="dvog-btn dvog-btn-icon" data-dvog-retry>' + ICON.reset + '<i>RETRY</i></button>',
        '        <button type="button" class="dvog-btn dvog-btn-primary" data-dvog-launch>' + ICON.play + '<i>LAUNCH</i></button>',
        '      </div>',
        '    </div>',
        '  </div>',

        '  <div class="dvog-screen" data-dvog-screen="loading">',
        '    <div class="dvog-spinner"></div>',
        '    <div class="dvog-eyebrow">DIREVULF OFFLINE</div>',
        '    <div class="dvog-progress"><i data-dvog-progress></i></div>',
        '    <p class="dvog-fine">LOADING LOCALLY — NO CONNECTION NEEDED</p>',
        '  </div>',

        '  <div class="dvog-screen" data-dvog-screen="title" hidden>',
        '    <div class="dvog-eyebrow">NO CONNECTION</div>',
        '    <h1 class="dvog-title">DIRE<span>VULF</span></h1>',
        '    <div class="dvog-eyebrow">JOIN THE PACK</div>',
        '    <p class="dvog-sub">You are offline, so the pack found something else to do. Draw a road and get the crate to the flag.</p>',
        '    <div class="dvog-screen-actions">',
        '      <button type="button" class="dvog-btn dvog-btn-primary" data-dvog-start>' + ICON.play + 'START</button>',
        '      <button type="button" class="dvog-btn" data-dvog-continue hidden>CONTINUE</button>',
        '    </div>',
        '    <div class="dvog-keys">',
        '      <span class="dvog-key"><b>DRAG</b> OR <b>TOUCH</b> TO DRAW</span>',
        '      <span class="dvog-key"><b>SPACE</b> TO LAUNCH</span>',
        '    </div>',
        '    <p class="dvog-fine">DIREVULF RETURNS AUTOMATICALLY WHEN YOU RECONNECT</p>',
        '  </div>',

        '  <div class="dvog-screen" data-dvog-screen="paused" hidden>',
        '    <div class="dvog-eyebrow">PAUSED</div>',
        '    <h1 class="dvog-title" style="font-size:clamp(24px,5vw,40px)">HOLD THE LINE</h1>',
        '    <div class="dvog-screen-actions">',
        '      <button type="button" class="dvog-btn dvog-btn-primary" data-dvog-resume>RESUME</button>',
        '      <button type="button" class="dvog-btn" data-dvog-restart>RESTART LEVEL</button>',
        '    </div>',
        '  </div>',

        '  <div class="dvog-screen" data-dvog-screen="cleared" hidden>',
        '    <div class="dvog-medal dvog-good">PACK DELIVERED</div>',
        '    <div class="dvog-eyebrow" data-dvog-clearname>—</div>',
        '    <div class="dvog-statgrid">',
        '      <div class="dvog-stat"><b data-dvog-ctime>0.0s</b><i>TIME</i></div>',
        '      <div class="dvog-stat"><b data-dvog-cmarks>0</b><i>PACK MARKS</i></div>',
        '      <div class="dvog-stat"><b data-dvog-cink>0</b><i>INK USED</i></div>',
        '      <div class="dvog-stat"><b data-dvog-ctries>1</b><i>RUNS</i></div>',
        '    </div>',
        '    <div class="dvog-screen-actions">',
        '      <button type="button" class="dvog-btn dvog-btn-primary" data-dvog-next>' + ICON.play + 'NEXT</button>',
        '      <button type="button" class="dvog-btn" data-dvog-replay>' + ICON.reset + 'REPLAY</button>',
        '    </div>',
        '  </div>',

        '  <div class="dvog-screen" data-dvog-screen="done" hidden>',
        '    <div class="dvog-eyebrow">EVERY ROAD DRAWN</div>',
        '    <h1 class="dvog-title">THE PACK<br><span>IS HOME</span></h1>',
        '    <div class="dvog-statgrid">',
        '      <div class="dvog-stat"><b data-dvog-dtime>0.0s</b><i>TOTAL TIME</i></div>',
        '      <div class="dvog-stat"><b data-dvog-dmarks>0</b><i>PACK MARKS</i></div>',
        '    </div>',
        '    <p class="dvog-sub">Shop by Fandom, Create Your Own, fandom clothing, accessories and Beyond the Pack are all waiting on the other side of a connection.</p>',
        '    <div class="dvog-screen-actions">',
        '      <button type="button" class="dvog-btn dvog-btn-primary" data-dvog-again>' + ICON.reset + 'PLAY AGAIN</button>',
        '    </div>',
        '  </div>',
        '</div>'
      ].join('');
      return root;
    },

    q: function (sel) { return this.root.querySelector('[data-dvog-' + sel + ']'); },

    showScreen: function (name) {
      this.screen = name;
      var all = this.root.querySelectorAll('[data-dvog-screen]');
      for (var i = 0; i < all.length; i++) {
        all[i].hidden = all[i].getAttribute('data-dvog-screen') !== name;
      }
      var hud = this.root.querySelector('.dvog-hud');
      hud.style.visibility = (name === null || name === 'paused') ? 'visible' : 'hidden';
      if (name === 'paused') { hud.style.visibility = 'hidden'; }
    },

    syncMute: function () {
      var b = this.q('mute');
      b.innerHTML = Sound.muted ? ICON.muted : ICON.sound;
      b.setAttribute('aria-pressed', Sound.muted ? 'true' : 'false');
    },

    syncHud: function (game) {
      if (!this.root || !game.level) { return; }
      this.q('level').textContent = (game.levelIndex + 1) + '/' + LEVELS.length + '  ' + game.level.name;
      this.q('hint').textContent = game.level.hint;
      var left = Math.round(game.inkLeft());
      this.q('ink').textContent = left;
      var fill = this.q('inkfill');
      var k = game.level.ink > 0 ? left / game.level.ink : 0;
      fill.style.transform = 'scaleX(' + k.toFixed(4) + ')';
      fill.className = 'dvog-ink-fill' + (left <= 0 ? ' dvog-out' : (k < 0.25 ? ' dvog-low' : ''));

      var launch = this.q('launch');
      launch.disabled = (game.state === 'run');
      launch.innerHTML = ICON.play + '<i>' + (game.state === 'run' ? 'RUNNING' : 'LAUNCH') + '</i>';
      this.q('undo').disabled = !game.strokes.length && !game.drawing;
      this.q('clear').disabled = !game.strokes.length && !game.drawing;
    },

    checkRotate: function () {
      if (!this.root) { return; }
      var chip = this.q('rotate');
      var portrait = window.innerHeight > window.innerWidth && window.innerWidth < 620;
      chip.hidden = !portrait;
    },

    /* ------------------------------------------------------------- wiring -- */
    wire: function () {
      var self = this;
      var canvas = this.q('canvas');
      var game = this.game;

      /* pointer: mouse, pen and touch through one path */
      this.on(canvas, 'pointerdown', function (e) {
        if (self.screen !== null) { return; }
        Sound.unlock();
        try { canvas.setPointerCapture(e.pointerId); } catch (err) { /* no-op */ }
        game.beginStroke(game.toWorld(e.clientX, e.clientY));
        e.preventDefault();
      });
      this.on(canvas, 'pointermove', function (e) {
        if (!game.drawing) { return; }
        var pts = (e.getCoalescedEvents && e.getCoalescedEvents().length) ? e.getCoalescedEvents() : [e];
        for (var i = 0; i < pts.length; i++) {
          game.extendStroke(game.toWorld(pts[i].clientX, pts[i].clientY));
        }
        e.preventDefault();
      });
      var up = function (e) {
        if (!game.drawing) { return; }
        game.endStroke();
        if (e) { e.preventDefault(); }
      };
      this.on(canvas, 'pointerup', up);
      this.on(canvas, 'pointercancel', up);
      this.on(window, 'blur', up);

      /* iOS Safari without pointer events falls back to touch */
      if (!window.PointerEvent) {
        this.on(canvas, 'touchstart', function (e) {
          if (self.screen !== null) { return; }
          Sound.unlock();
          game.beginStroke(game.toWorld(e.touches[0].clientX, e.touches[0].clientY));
          e.preventDefault();
        }, { passive: false });
        this.on(canvas, 'touchmove', function (e) {
          game.extendStroke(game.toWorld(e.touches[0].clientX, e.touches[0].clientY));
          e.preventDefault();
        }, { passive: false });
        this.on(canvas, 'touchend', up, { passive: false });
      }

      /* buttons */
      var tap = function (sel, fn) {
        self.on(self.q(sel), 'click', function (e) {
          e.preventDefault();
          Sound.unlock();
          fn();
        });
      };
      tap('mute', function () { Sound.setMuted(!Sound.muted); self.syncMute(); Sound.click(); });
      tap('undo', function () { game.undo(); });
      tap('clear', function () { game.clearInk(); });
      tap('retry', function () { game.retry(); });
      tap('launch', function () { game.launch(); });
      tap('start', function () { self.beginPlay(0); });
      tap('continue', function () { self.beginPlay(self.savedLevel()); });
      tap('resume', function () { self.showScreen(null); });
      tap('restart', function () { self.showScreen(null); game.retry(); game.clearInk(); });
      tap('next', function () { self.beginPlay(Math.min(LEVELS.length - 1, game.levelIndex + 1)); });
      tap('replay', function () { self.beginPlay(game.levelIndex); });
      tap('again', function () { game.totalTime = 0; self.totalMarks = 0; self.beginPlay(0); });

      /* keyboard */
      this.on(document, 'keydown', function (e) {
        if (!self.active) { return; }
        var k = e.key;
        var lower = typeof k === 'string' ? k.toLowerCase() : '';

        if (k === 'Escape') {
          e.preventDefault();
          if (self.screen === null) { self.showScreen('paused'); }
          else if (self.screen === 'paused') { self.showScreen(null); }
          return;
        }
        if (k === ' ' || k === 'Spacebar' || k === 'Enter') {
          e.preventDefault();
          Sound.unlock();
          if (self.screen === 'title') { self.q('start').click(); }
          else if (self.screen === 'cleared') { self.q('next').click(); }
          else if (self.screen === 'done') { self.q('again').click(); }
          else if (self.screen === 'paused') { self.showScreen(null); }
          else if (self.screen === null) { game.launch(); }
          return;
        }
        if (self.screen !== null) { return; }
        if (lower === 'r') { e.preventDefault(); game.retry(); }
        else if (lower === 'u' || (lower === 'z' && (e.ctrlKey || e.metaKey))) { e.preventDefault(); game.undo(); }
        else if (lower === 'c') { e.preventDefault(); game.clearInk(); }
        else if (lower === 'm') { e.preventDefault(); Sound.setMuted(!Sound.muted); self.syncMute(); }
        else if (lower === 'p') { e.preventDefault(); self.showScreen('paused'); }
      });

      /* first gesture anywhere unlocks audio (autoplay policy) */
      this.on(this.root, 'pointerdown', function () { Sound.unlock(); });

      /* while the game owns the screen, no storefront navigation can fire and
         drop the user onto a browser network-error page */
      this.on(document, 'click', function (e) {
        if (!self.active) { return; }
        if (self.root && self.root.contains(e.target)) { return; }
        var a = e.target && e.target.closest ? e.target.closest('a[href],button[type="submit"]') : null;
        if (a) { e.preventDefault(); e.stopPropagation(); }
      }, true);
      this.on(document, 'submit', function (e) {
        if (!self.active) { return; }
        if (self.root && self.root.contains(e.target)) { return; }
        e.preventDefault();
      }, true);

      /* resize / orientation */
      var onResize = function () {
        if (!self.active || !self.game) { return; }
        self.game.resize();
        self.checkRotate();
      };
      this.on(window, 'resize', onResize);
      this.on(window, 'orientationchange', onResize);

      /* tab hidden: silence the engine loop, do not keep audio running */
      this.on(document, 'visibilitychange', function () {
        if (!self.active) { return; }
        if (document.hidden) {
          Sound.setRoll(0);
          if (self.screen === null && self.game.state === 'run') { self.showScreen('paused'); }
        }
      });
    },

    savedLevel: function () {
      var v = parseInt(store('level') || '0', 10);
      if (isNaN(v)) { v = 0; }
      return clamp(v, 0, LEVELS.length - 1);
    },

    beginPlay: function (index) {
      this.showScreen(null);
      this.game.loadLevel(index);
      this.game.resize();
      Sound.click();
      store('level', String(index));
    },

    onWin: function (game) {
      var self = this;
      this.totalMarks = (this.totalMarks || 0) + game.marksGot;
      this.later(function () {
        if (!self.active) { return; }
        if (game.levelIndex >= LEVELS.length - 1) {
          self.q('dtime').textContent = game.totalTime.toFixed(1) + 's';
          self.q('dmarks').textContent = self.totalMarks + '/' + TOTAL_MARKS;
          self.showScreen('done');
          store('level', '0');
        } else {
          self.q('clearname').textContent = game.level.name;
          self.q('ctime').textContent = game.runTime.toFixed(1) + 's';
          self.q('cmarks').textContent = game.marksGot + '/' + game.marks.length;
          self.q('cink').textContent = Math.round(game.inkUsed);
          self.q('ctries').textContent = game.attempts;
          self.showScreen('cleared');
          store('level', String(game.levelIndex + 1));
        }
      }, 780);
    },

    /* --------------------------------------------------------------- open -- */
    open: function (reason) {
      if (this.active || this.booting) { return; }
      this.booting = true;
      this.reason = reason || 'offline';
      log('open', this.reason);

      this.prevTitle = document.title;
      document.title = 'DIREVULF — OFFLINE';
      document.documentElement.classList.add('dvog-locked');
      document.body.classList.add('dvog-locked');

      this.root = this.build();
      document.body.appendChild(this.root);
      this.active = true;
      this.booting = false;
      this.totalMarks = 0;

      var self = this;
      this.game = new Game(this.q('canvas'), {
        onState: function (g) { self.syncHud(g); },
        onWin: function (g) { self.onWin(g); }
      });
      this.game.loadLevel(0);
      this.game.resize();
      this.game.start();

      this.wire();
      this.syncMute();
      this.checkRotate();
      this.showScreen('loading');

      /* local "load" pass — mirrors the reference's spinner-then-title entry.
         Nothing is fetched; this is level + audio-buffer preparation. */
      var pct = 0;
      var bar = this.q('progress');
      var tick = window.setInterval(function () {
        pct = Math.min(100, pct + 12 + Math.random() * 16);
        bar.style.width = pct + '%';
        if (pct >= 100) {
          window.clearInterval(tick);
          self.later(function () {
            if (!self.active) { return; }
            var saved = self.savedLevel();
            self.q('continue').hidden = saved <= 0;
            self.showScreen('title');
          }, 220);
        }
      }, 90);
      this.timers.push(tick);
    },

    /* -------------------------------------------------------------- close -- */
    close: function (reason) {
      if (!this.active) { return; }
      log('close', reason);
      var self = this;
      this.active = false;

      var finish = function () {
        for (var i = 0; i < self.listeners.length; i++) {
          var L = self.listeners[i];
          try { L[0].removeEventListener(L[1], L[2], L[3]); } catch (e) { /* no-op */ }
        }
        self.listeners = [];
        for (var t = 0; t < self.timers.length; t++) {
          window.clearTimeout(self.timers[t]);
          window.clearInterval(self.timers[t]);
        }
        self.timers = [];

        if (self.game) { self.game.destroy(); self.game = null; }
        Sound.destroy();

        if (self.root && self.root.parentNode) { self.root.parentNode.removeChild(self.root); }
        self.root = null;
        self.screen = null;

        document.documentElement.classList.remove('dvog-locked');
        document.body.classList.remove('dvog-locked');
        if (self.prevTitle) { document.title = self.prevTitle; }
        log('teardown complete');
      };

      if (reason === 'online' && this.root) {
        var toast = document.createElement('div');
        toast.className = 'dvog-toast';
        toast.textContent = 'BACK ONLINE — RETURNING TO DIREVULF';
        this.root.querySelector('.dvog-stage').appendChild(toast);
        if (this.game) { this.game.state = 'draw'; }
        Sound.stopRoll();
        this.root.classList.add('dvog-is-closing');
        window.setTimeout(finish, 900);
      } else {
        finish();
      }
    }
  };

  /* ==========================================================================
     CONTROLLER — real browser network state only
     ========================================================================== */
  var forced = false;

  function offlineNow() { return navigator.onLine === false; }

  function evaluate(reason) {
    if (forced) { return; }
    if (offlineNow()) { Shell.open(reason); }
    else if (Shell.active) { Shell.close(reason); }
  }

  window.addEventListener('offline', function () { evaluate('offline-event'); });
  window.addEventListener('online', function () { evaluate('online'); });

  function boot() {
    /* internal dev switch: never reachable by normal browsing, and it does not
       change production behaviour. Use ?dvog-test=1 or the console API. */
    var testParam = false;
    try {
      testParam = window.location.search.indexOf('dvog-test=1') !== -1;
    } catch (e) { /* no-op */ }

    if (testParam) { forced = true; Shell.open('test-param'); return; }
    if (offlineNow()) { Shell.open('initial-offline'); }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* --------------------------------------------------------- public API --- */
  window.DIREVULF_OFFLINE_GAME = {
    version: VERSION,
    levels: LEVELS.length,
    isActive: function () { return Shell.active; },
    status: function () {
      return {
        version: VERSION,
        online: navigator.onLine,
        active: Shell.active,
        forced: forced,
        reason: Shell.reason,
        level: Shell.game ? Shell.game.levelIndex + 1 : null,
        state: Shell.game ? Shell.game.state : null,
        screen: Shell.screen,
        muted: Sound.muted,
        audio: Sound.ctx ? Sound.ctx.state : 'not-started'
      };
    },
    /* dev/QA only — production behaviour is driven purely by navigator.onLine */
    test: {
      open: function () { forced = true; Shell.open('test-api'); },
      close: function () { forced = false; Shell.close('test-api'); },
      release: function () { forced = false; evaluate('release'); },
      goto: function (n) {
        if (Shell.active && Shell.game) { Shell.beginPlay(clamp((n | 0) - 1, 0, LEVELS.length - 1)); }
      },
      win: function () { if (Shell.active && Shell.game) { Shell.game.state = 'run'; Shell.game.win(); } },
      rider: function () {
        if (!Shell.active || !Shell.game) { return null; }
        var r = Shell.game.rider;
        return { x: Math.round(r.x), y: Math.round(r.y), vx: Math.round(r.vx), vy: Math.round(r.vy),
                 grounded: r.grounded, gx: +r.gx.toFixed(3), gy: +r.gy.toFixed(3),
                 strokes: Shell.game.strokes.length, state: Shell.game.state };
      }
    }
  };

  log('ready v' + VERSION);
}());
